// The frame fold — pure helpers, the focus-history recorder, and `foldFrame`
// itself (the ONLY writer of frame factors).
//
// Split out of `app/frame.ts` (C1 hotspot decomposition). `foldFrame` is total
// over `FrameTransition`; the entry `../frame.ts` re-exports it at the stable
// path and seeds `initialFrame` from `focusedViewOf` (also exported here).
//
// Discipline (dev-ts): pure — no I/O, no Date/random, no React; total fold
// ending in `exhaustSwitchCase`; returns the SAME reference for a no-op so the
// host's render/effect machinery sees no phantom change.

import { exhaustSwitchCase } from "@bilda/utils";
import type { SecondarySlot } from "../../components/AppShell/AppShell.js";
import {
    chatConversationOf,
    dockConversation,
} from "../../components/Navigation/chatDock.js";
import {
    conversationView,
    type FocusHistory,
    goBack,
    goForward,
    type HistoryView,
    homeView,
    keptView,
    knowledgeView,
    materializeView,
    previewView,
    projectsView,
    promoteCurrent,
    recordFocus,
    sameView,
    searchView,
    settingsView,
    studioView,
} from "../../components/Navigation/focusHistory.js";
import {
    activateInPane,
    activateKeyInFocused,
    closePane,
    closePreviewInFocused,
    focusedLeaf,
    focusPane,
    focusPaneNext,
    focusPanePrevious,
    openPreviewInFocused,
    type PaneTree,
    paneTreeFromTabSet,
    previewEditedInFocused,
    promotePreviewInFocused,
    resizeDivider as resizeTreeDivider,
    type SplitEdge,
    sameKey,
    splitPane,
    tabSetOfPaneTree,
    withFocusedLeafTabs,
} from "../../components/Navigation/paneTree.js";
import {
    activateTab,
    type ConversationId,
    closeConversation,
    closeHome,
    closeKnowledge,
    closeOthers,
    closeProjects,
    closeQuality,
    closeSearch,
    closeSettings,
    closeStudio,
    closeTab,
    closeTabDiscarding,
    closeToRight,
    deactivateConversation,
    type FocusTarget,
    nextConversationId,
    openConversation,
    openHome,
    openKnowledge,
    openProjects,
    openQuality,
    openResource,
    openSearch,
    openSettings,
    openStudio,
    type ResourceRef,
    reconcileTabs,
    rememberClosed,
    selectSearchRow,
    setSearchQuery,
    type TabSet,
    takeLastClosed,
} from "../../components/Navigation/types.js";
import { receive } from "../messageQueue.js";
import { messageInput } from "../messages.js";
import {
    type FrameState,
    type FrameTransition,
    noOverlay,
    panelOverview,
} from "./types.js";

// === Fold helpers (pure, local) ==============================================

// Session recency is a working-memory aid, not a log.
const RECENTS_CAP = 5;

// The working set, DERIVED from the workspace's (single-leaf) pane tree. The
// TabSet is a view over the stored tree — the legacy folds still speak TabSet,
// the tree is the truth (P-CHROME step 2).
const activeTabs = (s: FrameState): TabSet => tabSetOfPaneTree(s.paneTree);

// Write a pane tree through (same reference in ⇒ same reference out, so a
// no-op fold never triggers a phantom write).
const withActivePaneTree = (s: FrameState, tree: PaneTree): FrameState =>
    tree === s.paneTree ? s : { ...s, paneTree: tree };

// Write the working set through — into the FOCUSED leaf only, preserving any
// other panes (multi-leaf-safe). For a single-leaf tree this is identical to
// rebuilding it.
const withActiveTabs = (s: FrameState, tabs: TabSet): FrameState =>
    withActivePaneTree(s, withFocusedLeafTabs(s.paneTree, tabs));

// Remember a closed resource on the reopen stack.
const withRemembered = (s: FrameState, resource: ResourceRef): FrameState => ({
    ...s,
    closedTabs: rememberClosed(s.closedTabs, resource),
});

// Remember a BATCH of closed resources (close-others / close-to-right), oldest
// first so the most-recently-closed sits on top of the reopen stack.
const withRememberedMany = (
    s: FrameState,
    resources: readonly ResourceRef[],
): FrameState => resources.reduce(withRemembered, s);

// Park an ACTIVE conversation tab on its neighbour (the tab stays open in
// the strip); a no-op — same reference — whenever the conversation is not
// the active tab, so callers cannot cause a phantom paneTree write.
const withParkedConversation = (s: FrameState): FrameState => {
    const tabs = activeTabs(s);
    const parked = deactivateConversation(tabs);
    return parked === tabs ? s : withActiveTabs(s, parked);
};

// Enter a conversation: it opens (or re-activates) in the working set (D3) and
// the chat panel opens on the Assistant. The fold's post-step
// (`withChatDocked`) then moves it out of the main view into the panel.
const intoFull = (
    s: FrameState,
    conversationId: ConversationId,
): FrameState => ({
    ...withActiveTabs(s, openConversation(activeTabs(s), conversationId)),
    openSecondary: "assistant",
});

// The open-resource arrow's whole effect: open the tab, bump session recency.
const openIntoFrame = (
    s: FrameState,
    ref: ResourceRef,
    target?: FocusTarget,
): FrameState => ({
    ...withActiveTabs(s, openResource(activeTabs(s), ref, target)),
    recents: [
        ref,
        ...s.recents.filter((r) => r.id !== ref.id).slice(0, RECENTS_CAP - 1),
    ],
});

// The summon-slot arrow's effect for the DOCK slots (Review / Quality /
// Audience). The Assistant is NOT a dock slot — it lives in its own tab
// (`enter-full-chat`), and the conversation slot was removed (subtractive
// audit). So this helper only opens a true dock slot, parking any active
// conversation tab.
const summonInto = (s: FrameState, slot: SecondarySlot): FrameState => ({
    ...withParkedConversation(s),
    openSecondary: slot,
});

// === Focus-history recorder (the ONE invariant keeper) =======================
// `history.current` must always equal the actually-focused view. Instead of
// scattering `recordFocus` across nine navigating arms (and risking a stale
// `current` after a close), a SINGLE post-fold step recomputes the focused view
// and records it — so the invariant holds structurally (make-illegal-states-
// unrepresentable: "current ≠ the focused view" is eliminated). Non-navigating
// transitions leave the focused view unchanged, so the recorder is a no-op for
// them (same reference). `nav-back`/`nav-forward` are excluded — they move the
// cursor, they do not push.

// The view the frame is currently focused on, or `null` when there is nothing
// to record (a quality report is pass-through — focusHistory.ts).
export const focusedViewOf = (tree: PaneTree): HistoryView | null => {
    const leaf = focusedLeaf(tree);
    const active = leaf.active;
    switch (active.type) {
        case "conversation":
            // A conversation records by id — nascent or durable alike (its
            // empty-vs-transcript rendering is derived downstream, not a
            // separate history place).
            return conversationView(active.id);
        case "settings":
            return settingsView;
        case "home":
            return homeView;
        case "studio":
            return studioView;
        case "projects":
            return projectsView;
        case "knowledge":
            return knowledgeView;
        case "search":
            return searchView;
        case "quality":
            // MVP shortcut: quality reports are NOT recorded in focus history —
            // a "pass-through, never navigated back to" treatment. Nav-back
            // skips over a quality tab to the view focused before it (the
            // material). Reachable any time from the material's quality panel,
            // so this loses no destination.
            return null;
        case "resource": {
            const id = active.id;
            if (leaf.preview !== null && leaf.preview.id === id)
                return previewView(leaf.preview);
            const kept = leaf.tabs.find(
                (x) => x.type === "resource" && x.resource.id === id,
            );
            return kept !== undefined && kept.type === "resource"
                ? keptView(kept.resource)
                : null;
        }
        default:
            return exhaustSwitchCase(active);
    }
};

// Record the focused view: a NEW place pushes; the SAME place only upgrades the
// register when a glance became kept (promoteCurrent — §3d.3), else no-op.
const recordView = (h: FocusHistory, view: HistoryView): FocusHistory => {
    if (!sameView(h.current, view)) return recordFocus(h, view);
    return view.type === "resource" && view.register === "kept"
        ? promoteCurrent(h)
        : h;
};

// Re-materialize a recalled view (nav-back / nav-forward) back into the frame:
// a RESOURCE view lands in the focused pane (as a glance or kept — D1's
// resurrection, even after it left the working set); a singleton view routes
// the CENTRE (the landing / full chat / Settings tab).
const applyRecalledView = (s: FrameState, view: HistoryView): FrameState => {
    switch (view.type) {
        case "resource":
            return withActivePaneTree(s, materializeView(s.paneTree, view));
        case "landing": {
            // Land on the landing: activate the first open conversation (a
            // nascent one renders as the hero composer), else open a fresh
            // nascent conversation. `landingView` is no longer produced by
            // `focusedViewOf` (a conversation records by id), so this arm is
            // only reached for a legacy-seeded history entry — kept total.
            const tabs = activeTabs(s);
            const firstConversation = tabs.conversationsOpen[0];
            return withActiveTabs(
                s,
                firstConversation !== undefined
                    ? activateTab(tabs, {
                          type: "conversation",
                          id: firstConversation,
                      })
                    : openConversation(
                          tabs,
                          nextConversationId(tabs.conversationsOpen),
                      ),
            );
        }
        case "conversation":
            return intoFull(s, view.id);
        case "settings":
            return withActiveTabs(s, openSettings(activeTabs(s)));
        case "home":
            return withActiveTabs(s, openHome(activeTabs(s)));
        case "studio":
            return withActiveTabs(s, openStudio(activeTabs(s)));
        case "projects":
            return withActiveTabs(s, openProjects(activeTabs(s)));
        case "knowledge":
            return withActiveTabs(s, openKnowledge(activeTabs(s)));
        case "search":
            return withActiveTabs(s, openSearch(activeTabs(s)));
        default:
            return exhaustSwitchCase(view);
    }
};

// The chat lives in the right panel, never in the main view (chatDock.ts): an
// arm that activated a conversation (entering a chat, a close falling back to
// one, nav-back to one) has it docked into the panel, and the main view goes
// back to what `prev` showed. The ONE place the invariant is kept, like the
// history recorder below.
const withChatDocked = (prev: FrameState, next: FrameState): FrameState => {
    const tabs = activeTabs(next);
    const docked = dockConversation(tabs, activeTabs(prev).active);
    return docked === tabs ? next : withActiveTabs(next, docked);
};

// The right panel's entity focus belongs to the conversation it was set in: a
// move to a DIFFERENT conversation (a new chat included) rests the panel back
// at the overview, so no chat inherits another's focus. Changing the main view
// keeps it.
const focusedConversationId = (tree: PaneTree): string | null =>
    chatConversationOf(tabSetOfPaneTree(tree));
const withConversationScopedFocus = (
    prev: FrameState,
    next: FrameState,
): FrameState => {
    const from = focusedConversationId(prev.paneTree);
    const to = focusedConversationId(next.paneTree);
    return next.panelFocus.type === "entity" && to !== null && from !== to
        ? { ...next, panelFocus: panelOverview }
        : next;
};

// === The fold ================================================================
// Total over FrameTransition; the ONLY writer of frame factors. Wraps the raw
// per-arm fold with the focus-history recorder so `history.current` tracks the
// focused view. Returns the SAME reference for a no-op so the host's render/
// effect machinery sees no phantom change.
export const foldFrame = (s: FrameState, t: FrameTransition): FrameState => {
    const next = withConversationScopedFocus(
        s,
        withChatDocked(s, rawFold(s, t)),
    );
    // The cursor-moving arms own `history` themselves — never re-record them.
    if (t.type === "nav-back" || t.type === "nav-forward") return next;
    const view = focusedViewOf(next.paneTree);
    if (view === null) return next;
    const history = recordView(next.history, view);
    return history === next.history ? next : { ...next, history };
};

// Focus one entity. A part of a material (a course's module, known from the
// library index) focuses its course, opened at the part (bd-lya), so the
// module reads in its place in the course, never as a loose material.
const entityFocus = (
    ref: ResourceRef,
    target?: FocusTarget,
): FrameState["panelFocus"] =>
    ref.type === "material" && ref.partOf !== undefined
        ? {
              type: "entity",
              ref: {
                  type: "material",
                  id: ref.partOf.rootId,
                  title: ref.partOf.title,
              },
              part: ref,
          }
        : { type: "entity", ref, target };

// The raw per-arm fold. Total over FrameTransition; the recorder above wraps it.
const rawFold = (s: FrameState, t: FrameTransition): FrameState => {
    switch (t.type) {
        case "collapse-rail":
            return s.nav === "collapsed" ? s : { ...s, nav: "collapsed" };
        case "expand-rail":
            return s.nav === "expanded" ? s : { ...s, nav: "expanded" };
        case "hide-rail":
            return s.nav === "hidden" ? s : { ...s, nav: "hidden" };
        case "toggle-rail":
            return {
                ...s,
                nav: s.nav === "expanded" ? "collapsed" : "expanded",
            };
        case "open-overlay":
            return { ...s, overlay: t.overlay };
        case "close-overlay":
            return s.overlay.type === "none" ? s : { ...s, overlay: noOverlay };
        case "summon-slot":
            // Only the legacy MATERIAL-scoped slots (Review / Quality) park the
            // active conversation (summonInto). The chat's panel READS the
            // conversation, so it merely sets the open slot.
            return t.slot === "review" || t.slot === "grounding"
                ? summonInto(s, t.slot)
                : { ...s, openSecondary: t.slot };
        case "close-secondary":
            // Dock slots (Review/Quality) don't touch the float any more.
            return s.openSecondary === null ? s : { ...s, openSecondary: null };
        case "summon-review":
            // Toggle the Review panel — collapse if already open, else summon.
            return s.openSecondary === "review"
                ? { ...s, openSecondary: null }
                : summonInto(s, "review");
        case "summon-quality":
            // Toggle the Quality panel (the `grounding` slot).
            return s.openSecondary === "grounding"
                ? { ...s, openSecondary: null }
                : summonInto(s, "grounding");
        case "summon-panel":
            // Toggle the chat's panel. Unlike the material-scoped slots it does
            // NOT park the active conversation (summonInto): the panel READS the
            // active conversation, so parking it would deactivate the very
            // conversation it shows (and collapse the dock).
            return s.openSecondary === "panel"
                ? { ...s, openSecondary: null }
                : { ...s, openSecondary: "panel" };
        case "focus-overview":
            // Zoom the right panel out to the conversation overview. A no-op
            // (same reference) when already at overview, so the host sees no
            // phantom write.
            return s.panelFocus.type === "overview"
                ? s
                : { ...s, panelFocus: { type: "overview" } };
        case "focus-entity":
            // Focus the right panel on one entity (a teleport / openRef). The
            // fold is the ONLY writer of panelFocus; `target` rides along
            // untouched for the render edge to resolve.
            return { ...s, panelFocus: entityFocus(t.ref, t.target) };
        case "show-statement-source":
            return {
                ...s,
                openSecondary: "panel",
                panelFocus: {
                    type: "entity",
                    ref: t.source,
                    target: t.target,
                    origin: t.origin,
                },
            };
        case "show-in-panel":
            // Open the panel on this entity (no toggle: an open panel stays
            // open).
            return {
                ...s,
                openSecondary: "panel",
                panelFocus: entityFocus(t.ref, t.target),
            };
        case "enter-full-chat":
            return intoFull(s, t.conversationId);
        case "create-material-from-source":
            // Pin the source as the chat's scope anchor, then open/re-activate
            // the FULL conversation TAB — the keystone room. The pin makes the
            // ContextSideCard's Scope name the source; the host's impure Context
            // write (introduce-to-context) is what surfaces the source in
            // Grounding and grounds the turn.
            return intoFull(
                { ...s, anchor: { type: "pinned", resource: t.source } },
                t.conversationId,
            );
        case "close-conversation-tab": {
            const tabs = activeTabs(s);
            if (!tabs.conversationsOpen.includes(t.conversationId)) return s;
            const closed = closeConversation(
                tabs,
                t.conversationId,
                t.freshConversationId,
            );
            // The chat panel always has a conversation: closing the last one
            // seeds the shell-minted fresh one there.
            return withActiveTabs(
                s,
                closed.conversationsOpen.length === 0
                    ? { ...closed, conversationsOpen: [t.freshConversationId] }
                    : closed,
            );
        }
        case "pin-chat":
            return { ...s, anchor: { type: "pinned", resource: t.resource } };
        case "unpin-chat":
            return { ...s, anchor: { type: "following" } };
        case "open-resource":
            return openIntoFrame(s, t.ref, t.target);
        case "open-resource-preview": {
            // A single-click glance: open `ref` as the focused pane's weak
            // preview (the slot, not `tabs` — it KEEPS nothing).
            return withActivePaneTree(
                s,
                openPreviewInFocused(s.paneTree, t.ref),
            );
        }
        case "promote-preview-tab":
            // Double-click the glance → keep it (the recorder flips the history
            // entry preview→kept in place).
            return withActivePaneTree(s, promotePreviewInFocused(s.paneTree));
        case "close-preview":
            return withActivePaneTree(s, closePreviewInFocused(s.paneTree));
        case "resource-edited":
            // Edit-must-keep: promotes the glance iff `id` IS the current
            // preview; a no-op (same tree) for a kept tab.
            return withActivePaneTree(
                s,
                previewEditedInFocused(s.paneTree, t.id),
            );
        case "nav-back": {
            const next = goBack(s.history);
            return next === undefined
                ? s
                : applyRecalledView({ ...s, history: next }, next.current);
        }
        case "nav-forward": {
            const next = goForward(s.history);
            return next === undefined
                ? s
                : applyRecalledView({ ...s, history: next }, next.current);
        }
        case "activate-tab":
            // Activating the conversation tab makes it the active tab (which is
            // what makes the conversation own the centre — the strip's click and
            // the ⌘-cycle share the enter arrow); a closed conversation tab
            // no-ops.
            if (t.tab.type === "conversation") {
                return activeTabs(s).conversationsOpen.includes(t.tab.id)
                    ? intoFull(s, t.tab.id)
                    : s;
            }
            // Leaf-level, not the TabSet fold (Fix A): `t.tab` may name the
            // focused pane's PREVIEW, which `TabSet.open` never carries —
            // `activateKeyInFocused` reads the Leaf directly so a digit/cycle
            // target resolved via `leafOrder` actually lands, preview
            // included. `t.tab`'s shape is identical to `TabKey`'s
            // member-for-member, so no conversion is needed (structural, not
            // a cast — the same pattern `activate-in-pane` already uses).
            return withActivePaneTree(
                s,
                activateKeyInFocused(s.paneTree, t.tab),
            );
        case "close-tab": {
            const tabs = activeTabs(s);
            const closing = tabs.open.find((x) => x.resource.id === t.id);
            const outcome = closeTab(tabs, t.id, t.freshConversationId);
            switch (outcome.type) {
                case "closed": {
                    const closedState = withActiveTabs(s, outcome.tabs);
                    return closing === undefined
                        ? closedState
                        : withRemembered(closedState, closing.resource);
                }
                case "unsaved":
                    return { ...s, confirmClose: outcome.tab };
                default:
                    return exhaustSwitchCase(outcome);
            }
        }
        case "close-active-tab": {
            // ⌘W / the desktop "Close tab" item: read the Leaf directly so a
            // preview-active tab is told apart from a kept-active one (both
            // present identically as `{type: "resource", id}` in the lossy
            // TabSet the old shell switch read — the confirmed no-op this
            // replaces). Recurses into the SAME fold with the already-correct
            // per-target intent rather than re-deriving its logic — one
            // definition of "how each kind of tab closes", picked from here.
            const leaf = focusedLeaf(s.paneTree);
            const isPreviewActive =
                leaf.preview !== null &&
                sameKey(leaf.active, { type: "resource", id: leaf.preview.id });
            if (isPreviewActive) {
                return withActivePaneTree(s, closePreviewInFocused(s.paneTree));
            }
            switch (leaf.active.type) {
                case "resource":
                    return foldFrame(s, {
                        type: "close-tab",
                        id: leaf.active.id,
                        freshConversationId: t.freshConversationId,
                    });
                case "conversation":
                    return foldFrame(s, {
                        type: "close-conversation-tab",
                        conversationId: leaf.active.id,
                        freshConversationId: t.freshConversationId,
                    });
                case "settings":
                    return foldFrame(s, {
                        type: "close-settings",
                        freshConversationId: t.freshConversationId,
                    });
                case "home":
                    return foldFrame(s, {
                        type: "close-home",
                        freshConversationId: t.freshConversationId,
                    });
                case "studio":
                    return foldFrame(s, {
                        type: "close-studio",
                        freshConversationId: t.freshConversationId,
                    });
                case "projects":
                    return foldFrame(s, {
                        type: "close-projects",
                        freshConversationId: t.freshConversationId,
                    });
                case "knowledge":
                    return foldFrame(s, {
                        type: "close-knowledge",
                        freshConversationId: t.freshConversationId,
                    });
                case "search":
                    return foldFrame(s, {
                        type: "close-search",
                        freshConversationId: t.freshConversationId,
                    });
                case "quality":
                    return foldFrame(s, {
                        type: "close-quality-report",
                        materialId: leaf.active.materialId,
                        freshConversationId: t.freshConversationId,
                    });
                default:
                    return exhaustSwitchCase(leaf.active);
            }
        }
        case "close-other-tabs": {
            const { tabs, closed } = closeOthers(activeTabs(s), t.id);
            return closed.length === 0
                ? s
                : withRememberedMany(withActiveTabs(s, tabs), closed);
        }
        case "close-tabs-to-right": {
            const { tabs, closed } = closeToRight(activeTabs(s), t.id);
            return closed.length === 0
                ? s
                : withRememberedMany(withActiveTabs(s, tabs), closed);
        }
        case "keep-unsaved-tab":
            return s.confirmClose === null ? s : { ...s, confirmClose: null };
        case "discard-unsaved-tab": {
            const tab = s.confirmClose;
            if (tab === null) return s;
            const closedState = withActiveTabs(
                s,
                closeTabDiscarding(
                    activeTabs(s),
                    tab.resource.id,
                    t.freshConversationId,
                ),
            );
            // A discarded close is still a close — reopen brings the
            // resource back (fresh, no buffer).
            return {
                ...withRemembered(closedState, tab.resource),
                confirmClose: null,
            };
        }
        case "reopen-closed-tab": {
            const taken = takeLastClosed(s.closedTabs);
            if (taken === undefined) return s;
            return openIntoFrame(
                { ...s, closedTabs: taken.rest },
                taken.resource,
            );
        }
        case "drop-resource-tab": {
            const tabs = activeTabs(s);
            if (!tabs.open.some((x) => x.resource.id === t.id)) return s;
            return withActiveTabs(
                s,
                closeTabDiscarding(tabs, t.id, t.freshConversationId),
            );
        }
        case "retitle-resource":
            return {
                ...withActivePaneTree(
                    s,
                    paneTreeFromTabSet({
                        ...activeTabs(s),
                        open: activeTabs(s).open.map((tab) =>
                            tab.resource.id === t.id
                                ? {
                                      ...tab,
                                      resource: {
                                          ...tab.resource,
                                          title: t.title,
                                      },
                                  }
                                : tab,
                        ),
                    }),
                ),
                recents: s.recents.map((r) =>
                    r.id === t.id ? { ...r, title: t.title } : r,
                ),
            };
        case "reconcile-tabs": {
            const current = activeTabs(s);
            const next = reconcileTabs(current, t.refs, t.freshConversationId);
            return next === current
                ? s
                : withActivePaneTree(s, paneTreeFromTabSet(next));
        }
        case "open-settings": {
            // Settings is a singleton TAB (P-CHROME step 4): set the section
            // and open/activate the one Settings tab in the working set —
            // re-opening focuses it, never duplicates.
            const sectioned: FrameState = { ...s, settingsSection: t.section };
            return withActiveTabs(
                sectioned,
                openSettings(activeTabs(sectioned)),
            );
        }
        case "close-settings": {
            // Close the Settings tab (its own unguarded intent — settings holds
            // no unsaved document); focus falls to its neighbour. A no-op when
            // it is not open.
            const tabs = activeTabs(s);
            if (!tabs.settingsOpen) return s;
            return withActiveTabs(
                s,
                closeSettings(tabs, t.freshConversationId),
            );
        }
        case "open-home":
            // Open/activate the Home overview singleton tab (dogfood F1) — the
            // Settings sibling, re-focusing the one tab, never duplicating.
            return withActiveTabs(s, openHome(activeTabs(s)));
        case "close-home": {
            // Close the Home tab (its own unguarded intent — Home holds no
            // unsaved document); focus falls to its neighbour. A no-op when it
            // is not open.
            const tabs = activeTabs(s);
            if (!tabs.homeOpen) return s;
            return withActiveTabs(s, closeHome(tabs, t.freshConversationId));
        }
        case "open-studio": // Open/activate the Studio canvas singleton tab (Studio v0) — the
            // Home sibling, re-focusing the one tab, never duplicating. A
            // `projectId` moves the author into that project.
            {
                const inProject: FrameState =
                    t.projectId === undefined || t.projectId === s.studioProject
                        ? s
                        : { ...s, studioProject: t.projectId };
                return withActiveTabs(
                    inProject,
                    openStudio(activeTabs(inProject)),
                );
            }
        case "close-studio": {
            const tabs = activeTabs(s);
            if (!tabs.studioOpen) return s;
            return withActiveTabs(s, closeStudio(tabs, t.freshConversationId));
        }
        case "open-projects":
            // Open/activate the Projects list singleton tab (#161) — the Home
            // sibling, re-focusing the one tab, never duplicating.
            return withActiveTabs(s, openProjects(activeTabs(s)));
        case "close-projects": {
            const tabs = activeTabs(s);
            if (!tabs.projectsOpen) return s;
            return withActiveTabs(
                s,
                closeProjects(tabs, t.freshConversationId),
            );
        }
        case "open-knowledge":
            // Open/activate the Knowledge bank singleton tab (#60) at the
            // given source (its viewer) or, without one, the list.
            return withActiveTabs(
                s,
                openKnowledge(activeTabs(s), {
                    selectedId: t.selectedId,
                    target: t.target,
                }),
            );
        case "close-knowledge": {
            const tabs = activeTabs(s);
            if (!tabs.knowledgeOpen) return s;
            return withActiveTabs(
                s,
                closeKnowledge(tabs, t.freshConversationId),
            );
        }
        case "open-search":
            // Open/activate the Search + Recent singleton tab (dogfood F6) — the
            // Home sibling, re-focusing the one tab, never duplicating. `t.kind`
            // (optional) pre-selects a facet — see ActiveSearch/OpenSearch;
            // `t.selectedId` pre-selects a row (F88 source-open).
            return withActiveTabs(
                s,
                openSearch(activeTabs(s), {
                    kind: t.kind,
                    selectedId: t.selectedId,
                    target: t.target,
                    query: t.query,
                }),
            );
        case "select-library-row":
            return withActiveTabs(
                s,
                selectSearchRow(activeTabs(s), t.selectedId, t.target),
            );
        case "set-library-query":
            return withActiveTabs(s, setSearchQuery(activeTabs(s), t.query));
        case "close-search": {
            // Close the Search tab (its own unguarded intent). A no-op when it
            // is not open.
            const tabs = activeTabs(s);
            if (!tabs.searchOpen) return s;
            return withActiveTabs(s, closeSearch(tabs, t.freshConversationId));
        }
        case "new-tab":
            // Open a fresh NASCENT conversation tab in the focused pane
            // (multiple allowed, no dedup) with the shell-minted id, and let the
            // conversation own the centre (it becomes the active tab). It renders
            // as the hero composer until its first message; no tab-kind swap
            // ever follows.
            return intoFull(s, t.conversationId);
        case "open-quality-report": {
            // Open/re-focus the material's full-quality report as its own tab in
            // the focused pane.
            return withActiveTabs(
                s,
                openQuality(activeTabs(s), {
                    type: "quality",
                    materialId: t.materialId,
                    title: t.title,
                }),
            );
        }
        case "close-quality-report": {
            const tabs = activeTabs(s);
            return tabs.qualityOpen.some((q) => q.materialId === t.materialId)
                ? withActiveTabs(
                      s,
                      closeQuality(tabs, t.materialId, t.freshConversationId),
                  )
                : s;
        }
        case "split-focused-pane": {
            // Split the focused pane, moving its ACTIVE tab into a new pane in
            // `direction`. A no-op when the active tab is home or the only tab
            // (splitPane guards both).
            const tree = s.paneTree;
            const edge: SplitEdge =
                t.direction === "up"
                    ? "top"
                    : t.direction === "down"
                      ? "bottom"
                      : t.direction;
            return withActivePaneTree(
                s,
                splitPane(
                    tree,
                    tree.focused,
                    focusedLeaf(tree).active,
                    edge,
                    t.newLeafId,
                    t.newBranchId,
                ),
            );
        }
        case "focus-pane-next":
            return withActivePaneTree(s, focusPaneNext(s.paneTree));
        case "focus-pane-previous":
            return withActivePaneTree(s, focusPanePrevious(s.paneTree));
        case "activate-in-pane": {
            // Focus the pane AND activate the tab in one gesture (a click in a
            // non-focused pane's strip). The activated tab becomes the active
            // one; when it is the conversation, that makes it own the centre.
            const next = activateInPane(
                focusPane(s.paneTree, t.pane),
                t.pane,
                t.tab,
            );
            return withActivePaneTree(s, next);
        }
        case "close-pane":
            return withActivePaneTree(
                s,
                closePane(s.paneTree, s.paneTree.focused),
            );
        case "resize-divider":
            return withActivePaneTree(
                s,
                resizeTreeDivider(
                    s.paneTree,
                    t.branch,
                    t.dividerIndex,
                    t.delta,
                ),
            );
        case "show-notice":
            return {
                ...s,
                messages: receive(s.messages, {
                    type: "show",
                    at: t.at,
                    input: messageInput(t.notice),
                }),
            };
        case "control-messages": {
            const messages = receive(s.messages, t.control);
            return messages === s.messages ? s : { ...s, messages };
        }
        default:
            return exhaustSwitchCase(t);
    }
};
